import type { EventMetadata } from "src/http/types";
import { canParticipateNonMlbMatchupCard } from "src/website-apps/shared/components/common-event-card/components/matchup-image/components/VerticalMatchupCard.utils";
import {
  canParticipateMlbMatchupCard,
  getTeamColor,
  type TeamColorSource,
} from "src/website-apps/shared/components/common-event-card/components/matchup-image/utils";

/**
 * Given a matchup event's metadata and the user's favorite team, returns the opposing
 * team's full name (falling back to its short name) — whichever of home/away is not the
 * favorite team.
 */
const getOpponentTeamName = (
  eventMetadata: Maybe<EventMetadata>,
  favoriteTeamCategoryId: number,
): string | undefined => {
  const { homeTeamId, awayTeamId } = eventMetadata?.sports ?? {};
  const opponentId = homeTeamId === favoriteTeamCategoryId ? awayTeamId : homeTeamId;
  if (opponentId === undefined) {
    return undefined;
  }
  const opponentMetadata = eventMetadata?.metadataForAllCategoriesInEvent?.[opponentId];
  return opponentMetadata?.name ?? opponentMetadata?.shortName ?? undefined;
};

/**
 * Given a matchup event's metadata and the user's favorite team, returns the opponent's name and
 * whether the favorite team is home, or undefined when the opponent can't be resolved.
 */
export const getMatchupOpponent = (
  eventMetadata: Maybe<EventMetadata>,
  favoriteTeamCategoryId: number,
): { opponentName: string; isFavoriteTeamHome: boolean } | undefined => {
  const opponentName = getOpponentTeamName(eventMetadata, favoriteTeamCategoryId);
  if (opponentName === undefined) {
    return undefined;
  }
  return { opponentName, isFavoriteTeamHome: eventMetadata?.sports?.homeTeamId === favoriteTeamCategoryId };
};

/**
 * Whether an event is a head-to-head matchup, as opposed to e.g. a season ticket package
 * covering the whole season with no single opponent. Reuses the same participation checks
 * VerticalMatchupCard/TeamCorner use, so "is this a matchup" stays consistent everywhere —
 * a bare homeTeamId/awayTeamId check isn't enough, since packages can carry sports metadata
 * without a resolvable opponent league.
 */
export const isMatchupEvent = (eventMetadata: Maybe<EventMetadata>): boolean =>
  canParticipateMlbMatchupCard({ eventMetadata }) || canParticipateNonMlbMatchupCard({ eventMetadata });

/** Logo, catalog photo, fallback initial and brand color for a team, shared by the header and the cards. */
export const getTeamBranding = (teamData: TeamColorSource) => ({
  logoUrl: teamData.theme?.partnerLogoLightSvgUrl ?? teamData.theme?.partnerLogoUrl ?? undefined,
  teamImageUrl: teamData.info?.teamImageUrl ?? undefined,
  abbreviation: teamData.info?.abbreviation ?? teamData.info?.shortName?.charAt(0),
  backgroundColor: getTeamColor(teamData),
});

/**
 * `createQueryFn` leaves an absolute base path untouched, which drops the request's query params, so
 * the team URL is reduced to its path before it is used as a base path.
 */
export const toRequestBasePath = (url: Maybe<string>): string | undefined => {
  if (!url) {
    return undefined;
  }
  return /^https?:\/\//.test(url) ? new URL(url).pathname : url;
};
